import { getCookie } from "cookies-next";
import get from "lodash/get";
import reduce from "lodash/reduce";

import {
  hasItem,
  productionQuestions,
} from "~/components/listings/data/production-faq-display-sections";
import { ListingData, useListingData } from "~/context/ListingPageDataContext";
import { IHostResponsiveness } from "~/types/api-types";
import { BaseNormalizedListing, IListingRules } from "~/types/local-types";
import { ProfileListingView } from "~/types/profile.types";
import { PSSK_COOKIE } from "~/utils/constants";

export type ListingFields = {
  listingId: string;
  category: string;
  cleaningFee: {
    value: number;
    obj_state: string;
  };
  currencySymbol?: string;
  dateCreated: string;
  dateModified: string;
  listingDayRateDiscount: {
    value: number;
    obj_state: string;
  };
  hasAddOns: boolean;
  hasPriceByDay: boolean;
  listingFavoriteCount?: number;
  metro: string;
  listingMinimumDuration: string;
  images: {
    externalId: string | undefined;
    description: string | null | undefined;
  }[];
  numOfAmenities: number;
  listingCapacity: number;
  attendeesMin: number;
  listingOwnerId: string;
  listingParentSpaceId: string;
  listingPrice: number;
  spaceReviewCount: number;
  spaceReviewStars: number;
  useType: string;
  isInstantBookActive: boolean;
  minAge: number | string;
  listingHostResponsiveness: IHostResponsiveness;
  pageTitle?: string;
  title?: string;
  listingCurrency?: string;
  listingAttendeePricing: {
    tiers: Array<unknown>;
  };
  productionDetails?: Record<string, unknown>;
  dayRateDiscountSet: boolean;
  parkingDetails?: {
    is_available: boolean;
    types: string[];
    description: string;
  };
};

export const useListingDataForNormalization = () => {
  const {
    data: {
      listingId,
      category,
      cleaningFee,
      currencySymbol,
      dateCreated,
      dateModified,
      listingDayRateDiscount,
      hasAddOns,
      hasPriceByDay,
      listingFavoriteCount,
      metro,
      listingMinimumDuration,
      images,
      numOfAmenities,
      listingCapacity,
      attendeesMin,
      listingOwnerId,
      listingParentSpaceId,
      listingPrice,
      spaceReviewCount,
      spaceReviewStars,
      useType,
      isInstantBookActive,
      minAge,
      listingHostResponsiveness,
      pageTitle,
      title,
      listingCurrency,
      listingAttendeePricing,
      productionDetails,
      dayRateDiscountSet,
      parkingDetails,
    },
  } = useListingData();
  const pickListingFields: ListingFields = {
    listingId,
    category,
    cleaningFee,
    currencySymbol,
    dateCreated,
    dateModified,
    listingDayRateDiscount,
    hasAddOns,
    hasPriceByDay,
    listingFavoriteCount,
    metro,
    listingMinimumDuration,
    images,
    numOfAmenities,
    listingCapacity,
    attendeesMin,
    listingOwnerId,
    listingParentSpaceId,
    listingPrice,
    spaceReviewCount,
    spaceReviewStars,
    useType,
    isInstantBookActive,
    minAge,
    listingHostResponsiveness,
    pageTitle,
    title,
    listingCurrency,
    listingAttendeePricing,
    productionDetails,
    dayRateDiscountSet,
    parkingDetails,
  };

  return pickListingFields;
};

export const normalizeListingObj = (data: ListingData | ListingFields) => {
  const listingImage = get(data, "images[0].externalId", "");
  const listingUseType = get(data, "uses[0]", "");
  const searchKW = getCookie(PSSK_COOKIE) as string;

  const normalizeListingObj: BaseNormalizedListing = {
    _id: data.listingId,
    listing_id: data.listingId,
    category: data.category,
    cleaning_fee_amount: data.cleaningFee?.value,
    cleaning_fee_set: data.cleaningFee?.obj_state === "ACTIVATED",
    currency_symbol: data.currencySymbol || "$",
    date_created: data.dateCreated,
    date_modified: data.dateModified,
    day_rate_discount_percent: data.listingDayRateDiscount?.value,
    day_rate_discount_set: data.dayRateDiscountSet,
    has_add_ons: data.hasAddOns,
    has_price_by_day: data.hasPriceByDay,
    listing_favorite_count: data.listingFavoriteCount || 0,
    metro: data.metro,
    minimum_duration: data.listingMinimumDuration || "0",
    num_images: data.images?.length || 0,
    num_of_amenities: data.numOfAmenities,
    number_guests: data.listingCapacity,
    attendees_min: data.attendeesMin || 0,
    ownerId: data.listingOwnerId,
    parentSpaceId: data.listingParentSpaceId,
    price: data.listingPrice,
    search_keyword: searchKW,
    space_review_count: data.spaceReviewCount,
    space_review_stars: data.spaceReviewStars,
    use_type: data.useType,
    is_instant_book_active: !!data.isInstantBookActive,
    min_age: 0 === data.minAge ? "All ages" : data.minAge,
    fandb_listing: get(data, "fandbRequired", false),
    host_response_time: data.listingHostResponsiveness?.hours,
    host_response_bucket: data.listingHostResponsiveness?.bucket,
    host_response_status: data.listingHostResponsiveness?.level_label,
  };

  if (data.cleaningFee) {
    const hasCleaningFee = "ACTIVATED" === get(data, "cleaningFee.obj_state");
    normalizeListingObj.cleaning_fee_set = hasCleaningFee;
    normalizeListingObj.cleaning_fee_amount = hasCleaningFee
      ? data.cleaningFee.value
      : 0;
  }
  if (data.dayRateDiscountSet) {
    const hasDayRate =
      "ACTIVATED" === get(data, "listingDayRateDiscount.obj_state");
    normalizeListingObj.day_rate_discount_set = hasDayRate;
    normalizeListingObj.day_rate_discount_percent = hasDayRate
      ? data.listingDayRateDiscount.value
      : 0;
  }
  if (listingImage) {
    normalizeListingObj.cover_img_id = listingImage;
  }

  if (data.pageTitle || data.title) {
    normalizeListingObj.listing_title = data.pageTitle
      ? data.pageTitle
      : data.title;
  }
  if (data.listingCurrency) {
    normalizeListingObj.currency = get(data, "prices[0].currency");
  }

  if (!data.useType && listingUseType) {
    normalizeListingObj.use_type = listingUseType;
  }
  normalizeListingObj.attendee_rate_tier_count = get(
    data,
    "listingAttendeePricing.tiers",
    [],
  ).length;

  // for the production segmentation P1 project
  const productionDetails = data.productionDetails;
  let areProductionSectionsIncluded = {};
  if (productionDetails && "Production" === normalizeListingObj.use_type) {
    areProductionSectionsIncluded = reduce(
      productionQuestions,
      (acc, sectionKeys, sectionName: string) => {
        acc["has_" + sectionName + "_info"] = hasItem(
          sectionKeys.keys as string[],
          productionDetails as unknown as IListingRules,
        );
        return acc;
      },
      {} as { [key: string]: boolean },
    );
  }

  const normalizedListing: BaseNormalizedListing = {
    ...normalizeListingObj,
    ...areProductionSectionsIncluded,
    has_parking_info: !!data.parkingDetails,
  };

  return normalizedListing;
};

export const normalizeListingObjFromProfilesPage = (
  listing: ProfileListingView,
): BaseNormalizedListing => {
  const coverImgId = get(listing, "images[0].external_id", "");
  const currency = get(listing, "prices[0].currency", "");
  const useType = get(listing, "uses[0].name", "") || listing.use_type;

  const normalizedListing: BaseNormalizedListing = {
    _id: listing._id,
    listing_id: listing._id,
    listing_favorite_count: listing.listing_favorite_count || 0,
    metro: listing.metro,
    number_guests: listing.number_of_guests,
    space_review_count: listing.space_review_count,
    space_review_stars: listing.space_review_stars ?? 0,
    use_type: useType,
  };

  if (coverImgId) {
    normalizedListing.cover_img_id = coverImgId;
  }
  if (currency) {
    normalizedListing.currency = currency;
  }
  if (listing.title) {
    normalizedListing.listing_title = listing.title;
  }

  return normalizedListing;
};
